/* eslint-disable react-refresh/only-export-components */
import React, { createContext, useContext, useState, useEffect, ReactNode } from "react";

import { getAllLevelData, type Quest } from "../../../api";

export interface LevelPath {
  id: number;
  name: string;
  description: string;
  collectionKey: string | null;
  status?: Level["status"];
}

export interface ActivePath {
  id: number;
  roleId: number;
  name: string;
  description: string;
  collectionKey: string | null;
  roleEnrollmentId: number;
  lessonsInEnrollment: number;
  lessonsPassed: number;
  estimatedTimeToComplete: number;
  completed: boolean;
  totalPoints: number;
  nextLesson: {
    id: number;
    lessonKey: string;
    name: string;
    description: string;
    estimatedTime: number;
    difficulty: string;
    lessonType: string;
    status: string;
    required: boolean;
  } | null;
  enrollmentType: string;
  levelRank: string | null;
}

export interface Level {
  id: number;
  name: string;
  description: string;
  status: "locked" | "in_progress" | "passed" | "abandoned";
  rankName: string;
  hasActivePaths?: boolean;
  visibleToUser?: boolean;
  activePath?: ActivePath | null;
  paths?: LevelPath[];
}

// QuestLevel is the context/UI type for quests — intentionally distinct from
// api/levels.ts Quest (which is the raw v1 API shape with role, lessons, price etc.).
// rankName is derived from Quest so the nullability stays in sync with the API type.
// Extending Omit<Level> keeps all other fields in sync with Level changes.
// role is only present on the legacy /quests response (QuestBucket falls back to
// role.id when a quest has no paths); the consolidated /levels response omits it.
export type QuestLevel = Omit<Level, "rankName"> &
  Pick<Quest, "rankName"> & { role?: Quest["role"] };

interface LevelContextType {
  levels: Level[];
  isLoading: boolean;
  error: Error | null;
  refetchLevelData: () => Promise<void>;
  levelDetails: Record<number, LevelPath[]>;
  quests: QuestLevel[];
}

const LevelContext = createContext<LevelContextType | undefined>(undefined);

const sortByName = <T extends { name?: string }>(items: T[] | null | undefined): T[] => {
  if (!Array.isArray(items)) return [];
  return [...items].sort((a, b) =>
    (a.name ?? "").localeCompare(b.name ?? "", undefined, { sensitivity: "base" })
  );
};

interface LevelContextProviderProps {
  children: ReactNode;
}

export const LevelContextProvider: React.FC<LevelContextProviderProps> = ({ children }) => {
  const [levels, setLevels] = useState<Level[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [levelDetails, setLevelDetails] = useState<Record<number, LevelPath[]>>({});
  const [error, setError] = useState<Error | null>(null);
  const [quests, setQuests] = useState<QuestLevel[]>([]);

  const filterAvailableLevels = (levels: Level[]) => {
    return levels.filter(level => level.status !== "locked");
  };

  const fetchConsolidated = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const data = await getAllLevelData();

      const courses: Level[] = data.courses || [];
      setLevels(filterAvailableLevels(courses));

      const details: Record<number, LevelPath[]> = {};
      courses.forEach((course: Level) => {
        details[course.id] = sortByName(course.paths);
      });
      setLevelDetails(details);

      setQuests(sortByName<QuestLevel>(data.quests ?? []));
    } catch (err) {
      console.error("Failed to fetch level data:", err);
      setError(err instanceof Error ? err : new Error("Failed to fetch level data"));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    void fetchConsolidated();
  }, []);

  const value: LevelContextType = {
    levels,
    isLoading,
    error,
    refetchLevelData: fetchConsolidated,
    levelDetails,
    quests,
  };

  return <LevelContext.Provider value={value}>{children}</LevelContext.Provider>;
};

export const useLevelContext = (): LevelContextType => {
  const context = useContext(LevelContext);
  if (context === undefined) {
    throw new Error("useLevelContext must be used within a LevelContextProvider");
  }
  return context;
};
